import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { prescriptionsService } from '@/services/prescriptions';
import { fulfillmentService } from '@/services/fulfillment';
import { drugsService } from '@/services/drugs';
import { getApiError } from '@/services/api';
import { useState, useEffect } from 'react';
import type {
  RemainingItem,
  FulfillPrescriptionInput,
  InteractionWarning,
  PrescriptionWithRelations,
  PatientSummary,
} from '@dpfs/shared';
import { formatPackagingHint } from '@dpfs/shared/packaging';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Ban,
  ShieldAlert,
  FileText,
  ImageIcon,
  Minus,
  Plus,
  X,
  Check,
  Info,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { DrugNoticeModal } from '@/components/DrugNoticeModal';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, StatusBadge, LoadingState, EmptyState, Spinner } from '@/components/ui';

// GET /prescriptions/:id returns more than the shared PrescriptionWithRelations type
// declares: the decrypted date of birth and the prescriber's contact details.
type PrescriptionDetail = PrescriptionWithRelations & {
  patient: PatientSummary & { dateOfBirth?: string | null };
  prescriber: PrescriptionWithRelations['prescriber'] & {
    specialty?: string | null;
    address?: string | null;
    phone?: string | null;
  };
};

const severityStyles: Record<string, string> = {
  SEVERE: 'bg-danger-50 border-danger-200 text-danger-800',
  MODERATE: 'bg-orange-50 border-orange-200 text-orange-800',
  MILD: 'bg-warning-50 border-warning-200 text-warning-800',
};

interface DispenseItem {
  prescriptionItemId: string;
  selected: boolean;
  quantity: number;
  substitutedDrugId?: string;
  substitutedDrugName?: string;
}

interface PendingDispense {
  input: FulfillPrescriptionInput;
  lines: { key: string; name: string; quantity: number; unit?: string | null; substitute?: string }[];
}

function computeAge(dob: string): number | null {
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  if (now.getMonth() < date.getMonth() || (now.getMonth() === date.getMonth() && now.getDate() < date.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

/** Drug name with notice / packaging shortcuts, controlled badge and DCI line. */
function ItemHeading({
  drug,
  label,
  onOpenNotice,
  htmlFor,
}: {
  drug?: { id: string; tradeName: string; dci?: string; strength?: string; controlledClass?: string | null } | null;
  label?: string | null;
  onOpenNotice: (drugId: string) => void;
  htmlFor?: string;
}) {
  const { t } = useTranslation();
  const Name = htmlFor ? 'label' : 'p';
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
        <Name htmlFor={htmlFor} className={clsx('break-words font-medium text-gray-900', htmlFor && 'cursor-pointer')}>
          {drug?.tradeName ?? label}
        </Name>
        {drug && (
          <>
            <button
              type="button"
              className="btn-icon h-8 w-8 text-primary-600"
              title={t('pharmacist.view.viewNotice')}
              aria-label={t('pharmacist.view.viewNotice')}
              onClick={(e) => {
                e.stopPropagation();
                onOpenNotice(drug.id);
              }}
            >
              <FileText className="h-4 w-4" />
            </button>
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(
                (drug.tradeName || '') + ' ' + (drug.strength || '') + ' boite'
              )}&tbm=isch`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-icon h-8 w-8 text-gray-400"
              title={t('pharmacist.view.viewPackaging')}
              aria-label={t('pharmacist.view.viewPackaging')}
              onClick={(e) => e.stopPropagation()}
            >
              <ImageIcon className="h-4 w-4" />
            </a>
          </>
        )}
        {drug?.controlledClass && (
          <span className={drug.controlledClass === 'A' ? 'badge-danger' : 'badge-warning'}>
            <ShieldAlert className="h-3 w-3" />
            {t('pharmacist.common.tableau', { class: drug.controlledClass })}
          </span>
        )}
      </div>
      {drug && (drug.dci || drug.strength) && (
        <p className="text-sm text-gray-600">{[drug.dci, drug.strength].filter(Boolean).join(' · ')}</p>
      )}
    </div>
  );
}

export function PrescriptionView() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'ar' ? 'ar-DZ' : 'fr-DZ';
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dispenseItems, setDispenseItems] = useState<DispenseItem[]>([]);
  const [genericsOpenFor, setGenericsOpenFor] = useState<string | null>(null);
  const [noticeModalDrugId, setNoticeModalDrugId] = useState<string | null>(null);
  const [pendingDispense, setPendingDispense] = useState<PendingDispense | null>(null);

  const {
    data,
    isLoading,
    isError: isPrescriptionError,
    error: prescriptionError,
  } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => prescriptionsService.getById(id!),
    enabled: !!id,
  });
  const prescription = data as PrescriptionDetail | undefined;

  // Only medication ordonnances are dispensed in pharmacy (the API rejects the others).
  const isMedication = !prescription?.documentType || prescription.documentType === 'MEDICATION';

  const { data: remainingData, isLoading: isLoadingRemaining } = useQuery({
    queryKey: ['prescription-remaining', id],
    queryFn: () => fulfillmentService.getRemainingItems(id!),
    enabled:
      !!id &&
      isMedication &&
      (prescription?.status === 'ACTIVE' ||
        prescription?.status === 'PARTIALLY_FULFILLED' ||
        // Also loaded once fully dispensed, for the delivery history.
        prescription?.status === 'FULFILLED'),
  });

  // Initialize dispense items when remaining data loads
  useEffect(() => {
    if (remainingData?.items) {
      setDispenseItems(
        remainingData.items
          .filter((item) => item.remainingQuantity > 0)
          .map((item) => ({
            prescriptionItemId: item.prescriptionItemId,
            selected: false,
            quantity: item.remainingQuantity,
          }))
      );
    }
  }, [remainingData]);

  // Find the drug ID for the currently open generics panel
  const openItemDrugId = genericsOpenFor
    ? remainingData?.items.find((i) => i.prescriptionItemId === genericsOpenFor)?.drug?.id
    : undefined;

  const { data: genericsData, isLoading: isLoadingGenerics } = useQuery({
    queryKey: ['drug-generics', openItemDrugId],
    queryFn: () => drugsService.getGenericAlternatives(openItemDrugId!),
    enabled: !!openItemDrugId,
  });

  const fulfillMutation = useMutation({
    mutationFn: (input: FulfillPrescriptionInput) => fulfillmentService.fulfill(id!, input),
    onSuccess: () => {
      setError(null);
      setSuccess(t('pharmacist.view.dispenseSuccess'));
      setPendingDispense(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
      queryClient.invalidateQueries({ queryKey: ['prescription-remaining', id] });
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
    },
    onError: (err) => {
      setError(getApiError(err).message);
      setPendingDispense(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  // Escape closes the confirmation dialog (unless a dispense is in flight).
  useEffect(() => {
    if (!pendingDispense) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !fulfillMutation.isPending) setPendingDispense(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pendingDispense, fulfillMutation.isPending]);

  const handleToggleItem = (prescriptionItemId: string) => {
    setDispenseItems((prev) =>
      prev.map((item) =>
        item.prescriptionItemId === prescriptionItemId
          ? { ...item, selected: !item.selected }
          : item
      )
    );
  };

  const handleQuantityChange = (prescriptionItemId: string, quantity: number) => {
    const remainingItem = remainingData?.items.find(
      (i) => i.prescriptionItemId === prescriptionItemId
    );
    const maxQuantity = remainingItem?.remainingQuantity || 0;
    const validQuantity = Math.max(1, Math.min(quantity, maxQuantity));

    setDispenseItems((prev) =>
      prev.map((item) =>
        item.prescriptionItemId === prescriptionItemId
          ? { ...item, quantity: validQuantity }
          : item
      )
    );
  };

  const handleSelectAll = () => {
    setDispenseItems((prev) => prev.map((item) => ({ ...item, selected: true })));
  };

  const handleDeselectAll = () => {
    setDispenseItems((prev) => prev.map((item) => ({ ...item, selected: false })));
  };

  const handleSelectGeneric = (prescriptionItemId: string, drugId: string | undefined, drugName?: string) => {
    setDispenseItems((prev) =>
      prev.map((item) =>
        item.prescriptionItemId === prescriptionItemId
          ? { ...item, substitutedDrugId: drugId, substitutedDrugName: drugName }
          : item
      )
    );
    setGenericsOpenFor(null);
  };

  const remainingById = (prescriptionItemId: string) =>
    remainingData?.items.find((i) => i.prescriptionItemId === prescriptionItemId);

  // Both dispense actions open a confirmation first: a fulfillment cannot be undone.
  const handleFulfill = () => {
    const selectedItems = dispenseItems.filter((item) => item.selected);
    if (selectedItems.length === 0) {
      setError(t('fulfillment.selectItemsToDispense'));
      return;
    }

    const input: FulfillPrescriptionInput = {
      fulfilledItems: selectedItems.map((item) => ({
        prescriptionItemId: item.prescriptionItemId,
        quantityDispensed: item.quantity,
        substitutedDrugId: item.substitutedDrugId,
      })),
    };

    setSuccess(null);
    setPendingDispense({
      input,
      lines: selectedItems.map((item) => {
        const remaining = remainingById(item.prescriptionItemId);
        return {
          key: item.prescriptionItemId,
          name: remaining?.drug?.tradeName ?? '—',
          quantity: item.quantity,
          unit: remaining?.quantityUnit,
          substitute: item.substitutedDrugId ? item.substitutedDrugName : undefined,
        };
      }),
    });
  };

  const handleFulfillAll = () => {
    if (!remainingData?.items) return;

    const itemsToFulfill = remainingData.items.filter((item) => item.remainingQuantity > 0);
    if (itemsToFulfill.length === 0) return;

    const input: FulfillPrescriptionInput = {
      fulfilledItems: itemsToFulfill.map((item) => ({
        prescriptionItemId: item.prescriptionItemId,
        quantityDispensed: item.remainingQuantity,
      })),
    };

    setSuccess(null);
    setPendingDispense({
      input,
      lines: itemsToFulfill.map((item) => ({
        key: item.prescriptionItemId,
        name: item.drug?.tradeName ?? '—',
        quantity: item.remainingQuantity,
        unit: item.quantityUnit,
      })),
    });
  };

  const confirmDispense = () => {
    if (!pendingDispense || fulfillMutation.isPending) return;
    fulfillMutation.mutate(pendingDispense.input);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (!prescription) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader
          title={t('pharmacist.view.title')}
          backTo="/pharmacist/search"
          backLabel={t('pharmacist.view.backToSearch')}
        />
        <div className="card p-0">
          <EmptyState
            icon={FileText}
            title={t('pharmacist.view.notFound')}
            description={isPrescriptionError ? getApiError(prescriptionError).message : undefined}
          />
        </div>
      </div>
    );
  }

  const canFulfill =
    isMedication && (prescription.status === 'ACTIVE' || prescription.status === 'PARTIALLY_FULFILLED');
  const isFullyFulfilled = prescription.status === 'FULFILLED';
  const isPartiallyFulfilled = prescription.status === 'PARTIALLY_FULFILLED';
  const hasRemainingItems = remainingData?.items.some((item) => item.remainingQuantity > 0);
  const hasFulfillmentHistory = remainingData?.fulfillmentHistory && remainingData.fulfillmentHistory.length > 0;
  const remainingCount = remainingData?.items.filter((i) => i.remainingQuantity > 0).length || 0;
  const selectedCount = dispenseItems.filter((i) => i.selected).length;

  // Calculate totals for status display
  const totalItems = prescription.items?.length || 0;
  const deliveredItems = remainingData?.items.filter((item) => item.dispensedQuantity > 0).length || 0;

  // The history summary carries no unit: look it up from the prescription's items.
  const unitForDrugName = (drugName: string) =>
    remainingData?.items.find((i) => i.drug?.tradeName === drugName)?.quantityUnit;

  const age = prescription.patient?.dateOfBirth ? computeAge(prescription.patient.dateOfBirth) : null;
  const interactionWarnings = (prescription.interactionWarnings ?? []) as InteractionWarning[];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        title={t('pharmacist.view.title')}
        description={t('pharmacist.common.rxNumber', { id: prescription.id.slice(-8) })}
        backTo="/pharmacist/search"
        backLabel={t('pharmacist.view.backToSearch')}
        actions={<StatusBadge status={prescription.status} className="px-2.5 py-1 text-sm" />}
      />

      <div className="space-y-4">
        {error && (
          <div className="alert-danger" role="alert">
            <XCircle className="h-5 w-5 shrink-0" />
            <p className="min-w-0 flex-1 break-words">{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="-m-1 rounded p-1 hover:bg-danger-100"
              aria-label={t('pharmacist.common.dismiss')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="alert-success" role="status">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <p className="min-w-0 flex-1">{success}</p>
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="-m-1 rounded p-1 hover:bg-success-100"
              aria-label={t('pharmacist.common.dismiss')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {!isMedication && (
          <div className="alert-info">
            <Info className="h-5 w-5 shrink-0" />
            <p>{t('pharmacist.view.nonMedication')}</p>
          </div>
        )}

        {isFullyFulfilled && (
          <div className="alert-success">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <div>
              <h3 className="font-semibold">{t('fulfillment.fulfilled')}</h3>
              {remainingData?.fulfillmentHistory?.[0] && (
                <p>
                  {t('pharmacist.view.lastDelivery', {
                    date: new Date(remainingData.fulfillmentHistory[0].fulfilledAt).toLocaleString(dateLocale),
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        {isPartiallyFulfilled && (
          <div className="alert-warning">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <h3 className="font-semibold">{t('fulfillment.partiallyFulfilled')}</h3>
              {remainingData && (
                <p>{t('pharmacist.view.deliveredCount', { delivered: deliveredItems, total: totalItems })}</p>
              )}
            </div>
          </div>
        )}

        {prescription.status === 'EXPIRED' && (
          <div className="alert-warning">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <h3 className="font-semibold">{t('fulfillment.expired')}</h3>
              <p>
                {t('pharmacist.view.expiredOn', {
                  date: new Date(prescription.expiresAt).toLocaleDateString(dateLocale),
                })}
              </p>
            </div>
          </div>
        )}

        {prescription.status === 'CANCELLED' && (
          <div className="alert-danger">
            <Ban className="h-5 w-5 shrink-0" />
            <h3 className="font-semibold">{t('pharmacist.view.cancelled')}</h3>
          </div>
        )}

        {/* Drug Interaction Warnings from Prescriber */}
        {interactionWarnings.length > 0 && (
          <div className="card border-orange-200 p-4 sm:p-5">
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-orange-800">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              {t('interactions.title')}
            </h3>
            {prescription.warningsAcknowledgedAt && (
              <p className="mb-3 text-sm text-gray-600">
                {t('interactions.acknowledgedBy')}{' '}
                {new Date(prescription.warningsAcknowledgedAt).toLocaleDateString(dateLocale)}
              </p>
            )}
            <div className="mt-3 space-y-2">
              {interactionWarnings.map((interaction, i) => (
                <div
                  key={i}
                  className={clsx('rounded-lg border p-3', severityStyles[interaction.severity] ?? severityStyles.MILD)}
                >
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <p className="min-w-0 break-words text-sm font-medium">
                      {interaction.drug1?.tradeName} + {interaction.drug2?.tradeName}
                    </p>
                    <span className="shrink-0 rounded bg-white/60 px-2 py-0.5 text-xs font-semibold">
                      {t(`interactions.${interaction.severity.toLowerCase()}`)}
                    </span>
                  </div>
                  <p className="text-sm">{interaction.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Controlled Substance Alert */}
        {(() => {
          const items = (canFulfill && remainingData?.items) ? remainingData.items : prescription.items || [];
          const controlledItems = items.filter((item: any) => item.drug?.controlledClass);
          if (controlledItems.length === 0) return null;
          return (
            <div className="card border-warning-200 p-4 sm:p-5">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-warning-800">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                {t('controlled.prescriptionContains')}
              </h3>
              <div className="space-y-2">
                {controlledItems.map((item: any, i: number) => (
                  <div
                    key={i}
                    className={clsx(
                      'rounded-lg border p-3',
                      item.drug?.controlledClass === 'A'
                        ? 'border-danger-200 bg-danger-50'
                        : 'border-warning-200 bg-warning-50'
                    )}
                  >
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900">
                      {item.drug?.tradeName}
                      <span className={item.drug?.controlledClass === 'A' ? 'badge-danger' : 'badge-warning'}>
                        {t(`controlled.tableau${item.drug?.controlledClass}`)}
                      </span>
                    </p>
                    <p className="mt-1 text-sm text-gray-700">
                      {item.drug?.controlledClass === 'A' && t('controlled.tableauAPharmacist')}
                      {item.drug?.controlledClass === 'B' && t('controlled.tableauBPharmacist')}
                      {item.drug?.controlledClass === 'C' && t('controlled.tableauCPharmacist')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Patient & prescriber */}
        <div className="card grid gap-5 p-4 sm:grid-cols-2 sm:p-5">
          <div className="min-w-0">
            <h2 className="section-title mb-1.5">{t('prescription.patient')}</h2>
            <p className="break-words text-lg font-medium text-gray-900">
              {prescription.patient?.lastName} {prescription.patient?.firstName}
            </p>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-gray-500">
              {prescription.patient?.gender && (
                <span>{prescription.patient.gender === 'MALE' ? t('patient.male') : t('patient.female')}</span>
              )}
              {age != null && <span>{t('pharmacist.view.age', { count: age })}</span>}
              {prescription.patient && (
                <span>
                  {(() => {
                    const pid = patientIdentifier(prescription.patient);
                    return `${t(pid.typeKey)}: ${pid.masked}`;
                  })()}
                </span>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <h2 className="section-title mb-1.5">{t('prescription.prescriber')}</h2>
            <p className="break-words font-medium text-gray-900">
              {t('pharmacist.common.doctorName', {
                name: `${prescription.prescriber?.firstName ?? ''} ${prescription.prescriber?.lastName ?? ''}`.trim(),
              })}
            </p>
            {prescription.prescriber?.specialty && (
              <p className="text-sm text-gray-600">{prescription.prescriber.specialty}</p>
            )}
            {prescription.prescriber?.address && (
              <p className="break-words text-sm text-gray-500">{prescription.prescriber.address}</p>
            )}
            {prescription.prescriber?.phone && (
              <p className="text-sm text-gray-500">
                {t('pharmacist.view.phone')}:{' '}
                <a href={`tel:${prescription.prescriber.phone}`} className="link" dir="ltr">
                  {prescription.prescriber.phone}
                </a>
              </p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              {t('pharmacist.view.prescribedOn', {
                date: new Date(prescription.createdAt).toLocaleDateString(dateLocale),
              })}
            </p>
          </div>
        </div>

        {/* Remaining Items to Dispense */}
        {canFulfill && (isLoadingRemaining || hasRemainingItems) && (
          <div className="card p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="card-title">{t('fulfillment.remainingItems')}</h2>
              {!isLoadingRemaining && dispenseItems.length > 0 && (
                dispenseItems.some((i) => !i.selected) ? (
                  <button type="button" onClick={handleSelectAll} className="btn-ghost btn-sm text-primary-700">
                    {t('pharmacist.view.selectAll')}
                  </button>
                ) : (
                  <button type="button" onClick={handleDeselectAll} className="btn-ghost btn-sm">
                    {t('pharmacist.view.deselectAll')}
                  </button>
                )
              )}
            </div>
            {isLoadingRemaining ? (
              <div className="flex justify-center py-6">
                <Spinner className="h-6 w-6" />
              </div>
            ) : (
              <div className="space-y-3">
                {remainingData?.items
                  .filter((item) => item.remainingQuantity > 0)
                  .map((item: RemainingItem) => {
                    const dispenseItem = dispenseItems.find(
                      (d) => d.prescriptionItemId === item.prescriptionItemId
                    );
                    const checkboxId = `dispense-${item.prescriptionItemId}`;
                    const packagingHint = formatPackagingHint(
                      item.remainingQuantity,
                      item.drug?.packagingSize,
                      item.drug?.dosageForm
                    );
                    return (
                      <div
                        key={item.prescriptionItemId}
                        className={clsx(
                          'rounded-lg border-2 p-3 transition-colors sm:p-4',
                          dispenseItem?.selected ? 'border-primary-400 bg-primary-50/60' : 'border-gray-200 bg-white'
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            id={checkboxId}
                            type="checkbox"
                            checked={dispenseItem?.selected || false}
                            onChange={() => handleToggleItem(item.prescriptionItemId)}
                            className="mt-1 h-6 w-6 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                              <div className="min-w-0">
                                <ItemHeading
                                  drug={item.drug}
                                  onOpenNotice={setNoticeModalDrugId}
                                  htmlFor={checkboxId}
                                />
                                {item.drug?.packagingDescription && (
                                  <p className="text-xs text-gray-400">{item.drug.packagingDescription}</p>
                                )}
                              </div>
                              <div className="shrink-0 sm:text-end">
                                <span className="badge-warning text-sm">
                                  {t('fulfillment.remaining')}: {item.remainingQuantity}
                                  {item.quantityUnit ? ` ${item.quantityUnit}` : ''}
                                </span>
                                {packagingHint && <p className="mt-1 text-xs text-gray-500">{packagingHint}</p>}
                              </div>
                            </div>
                            {[item.dosage, item.frequency, item.duration].some(Boolean) && (
                              <p className="mt-1.5 text-sm text-gray-700">
                                {[item.dosage, item.frequency, item.duration].filter(Boolean).join(' · ')}
                              </p>
                            )}
                            {item.instructions && (
                              <p className="mt-1 break-words text-sm italic text-gray-500">{item.instructions}</p>
                            )}
                            {!item.substitutionAllowed && (
                              <p className="mt-1 flex items-center gap-1 text-xs font-medium text-danger-600">
                                <ShieldAlert className="h-3.5 w-3.5" />
                                {t('pharmacist.view.substitutionNotAllowed')}
                              </p>
                            )}
                            {dispenseItem?.selected && (
                              <>
                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                  <label htmlFor={`qty-${item.prescriptionItemId}`} className="text-sm text-gray-600">
                                    {t('fulfillment.quantityToDispense')}
                                  </label>
                                  <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white shadow-sm">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleQuantityChange(item.prescriptionItemId, dispenseItem.quantity - 1)
                                      }
                                      disabled={dispenseItem.quantity <= 1}
                                      className="flex h-10 w-10 items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                      aria-label={t('pharmacist.view.decrease')}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </button>
                                    <input
                                      id={`qty-${item.prescriptionItemId}`}
                                      type="number"
                                      inputMode="numeric"
                                      min={1}
                                      max={item.remainingQuantity}
                                      value={dispenseItem.quantity}
                                      onChange={(e) =>
                                        handleQuantityChange(
                                          item.prescriptionItemId,
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                      className="h-10 w-16 border-x border-gray-300 text-center text-base font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleQuantityChange(item.prescriptionItemId, dispenseItem.quantity + 1)
                                      }
                                      disabled={dispenseItem.quantity >= item.remainingQuantity}
                                      className="flex h-10 w-10 items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                      aria-label={t('pharmacist.view.increase')}
                                    >
                                      <Plus className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <span className="text-xs text-gray-500">/ {item.remainingQuantity}</span>
                                </div>

                                {/* Generic substitution */}
                                {item.substitutionAllowed && (
                                  <div className="mt-3">
                                    {dispenseItem.substitutedDrugId && (
                                      <div className="mb-2 flex items-center gap-2 rounded-md bg-success-50 px-3 py-2 text-sm text-success-800">
                                        <Check className="h-4 w-4 shrink-0" />
                                        <span className="min-w-0 flex-1 truncate">
                                          {t('generics.selected')}:{' '}
                                          <span className="font-medium">{dispenseItem.substitutedDrugName || '…'}</span>
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleSelectGeneric(item.prescriptionItemId, undefined)}
                                          className="-m-1 rounded p-1 text-success-700 hover:bg-success-100"
                                          aria-label={t('pharmacist.view.removeSubstitution')}
                                          title={t('pharmacist.view.removeSubstitution')}
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setGenericsOpenFor(
                                          genericsOpenFor === item.prescriptionItemId ? null : item.prescriptionItemId
                                        )
                                      }
                                      aria-expanded={genericsOpenFor === item.prescriptionItemId}
                                      className="btn-ghost btn-sm -ms-3 text-primary-700"
                                    >
                                      {genericsOpenFor === item.prescriptionItemId
                                        ? t('generics.hideAlternatives')
                                        : t('generics.suggestGenerics')}
                                    </button>

                                    {genericsOpenFor === item.prescriptionItemId && (
                                      <div className="mt-2 rounded-lg border border-primary-200 bg-white p-3">
                                        <h4 className="section-title mb-2">{t('generics.availableAlternatives')}</h4>
                                        {isLoadingGenerics ? (
                                          <div className="flex justify-center py-2">
                                            <Spinner />
                                          </div>
                                        ) : !genericsData?.alternatives?.length ? (
                                          <p className="text-sm text-gray-500">{t('generics.noAlternatives')}</p>
                                        ) : (
                                          <div className="space-y-2">
                                            {genericsData.alternatives.map((alt) => (
                                              <button
                                                key={alt.id}
                                                type="button"
                                                onClick={() =>
                                                  handleSelectGeneric(item.prescriptionItemId, alt.id, alt.tradeName)
                                                }
                                                className={clsx(
                                                  'w-full rounded-lg border p-3 text-start transition-colors hover:bg-primary-50',
                                                  dispenseItem.substitutedDrugId === alt.id
                                                    ? 'border-primary-400 bg-primary-50'
                                                    : 'border-gray-200'
                                                )}
                                              >
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-gray-900">
                                                      {alt.tradeName}
                                                    </p>
                                                    <p className="truncate text-xs text-gray-500">{alt.manufacturer}</p>
                                                  </div>
                                                  <div className="shrink-0 text-end">
                                                    {alt.referencePrice != null && (
                                                      <p className="text-sm font-medium text-gray-900 tabular-nums">
                                                        {(alt.referencePrice / 100).toFixed(2)} DA
                                                      </p>
                                                    )}
                                                    {alt.priceDifference != null && alt.priceDifference < 0 && (
                                                      <p className="text-xs font-medium text-success-700">
                                                        {t('generics.savings')}:{' '}
                                                        {Math.abs(alt.priceDifference / 100).toFixed(2)} DA
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}

        {/* All items delivered message */}
        {canFulfill && !hasRemainingItems && remainingData && (
          <div className="alert-success">
            <CheckCircle2 className="h-5 w-5 shrink-0" />
            <p>{t('fulfillment.noRemainingItems')}</p>
          </div>
        )}

        {/* All Medications (for reference when fully fulfilled or non-fulfillable) */}
        {(!canFulfill || !hasRemainingItems) && (
          <div className="card p-4 sm:p-5">
            <h2 className="card-title mb-3">
              {isMedication ? t('prescription.medications') : t('prescription.orders')}
            </h2>
            <div className="space-y-3">
              {prescription.items?.map((item: any, index: number) => {
                const packagingHint = item.drug
                  ? formatPackagingHint(item.quantity, item.drug?.packagingSize, item.drug?.dosageForm)
                  : null;
                return (
                  <div key={index} className="rounded-lg border border-gray-200 p-3 sm:p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <ItemHeading drug={item.drug} label={item.itemLabel} onOpenNotice={setNoticeModalDrugId} />
                      {item.quantity != null && (
                        <div className="shrink-0 sm:text-end">
                          <span className="badge-gray text-sm">
                            {item.quantity} {item.quantityUnit || '×'}
                          </span>
                          {packagingHint && <p className="mt-1 text-xs text-gray-500">{packagingHint}</p>}
                        </div>
                      )}
                    </div>
                    {[item.dosage, item.frequency, item.duration].some(Boolean) && (
                      <p className="mt-1.5 text-sm text-gray-700">
                        {[item.dosage, item.frequency, item.duration].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {item.instructions && (
                      <p className="mt-1 break-words text-sm italic text-gray-500">{item.instructions}</p>
                    )}
                    {item.drug && !item.substitutionAllowed && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-medium text-danger-600">
                        <ShieldAlert className="h-3.5 w-3.5" />
                        {t('pharmacist.view.substitutionNotAllowed')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Delivery History */}
        {hasFulfillmentHistory && (
          <div className="card p-4 sm:p-5">
            <h2 className="card-title mb-3">{t('fulfillment.deliveryHistory')}</h2>
            <ol className="space-y-3">
              {remainingData.fulfillmentHistory.map((fulfillment) => (
                <li key={fulfillment.id} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-sm">
                    <span className="font-medium text-gray-900">
                      {new Date(fulfillment.fulfilledAt).toLocaleString(dateLocale, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </span>
                    <span className="text-gray-500">
                      {fulfillment.pharmacist?.firstName} {fulfillment.pharmacist?.lastName}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {fulfillment.items.map((item, idx) => {
                      const unit = unitForDrugName(item.drugName);
                      return (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="h-4 w-4 shrink-0 text-success-600" />
                          <span className="min-w-0 flex-1 truncate">{item.drugName}</span>
                          <span className="shrink-0 font-medium tabular-nums">
                            {item.quantity ?? '—'} {unit || '×'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Diagnosis / Notes */}
        {(prescription.diagnosis || prescription.notes) && (
          <div className="card space-y-3 p-4 sm:p-5">
            {prescription.diagnosis && (
              <div>
                <h2 className="section-title mb-1">{t('prescription.diagnosis')}</h2>
                <p className="break-words text-gray-700">{prescription.diagnosis}</p>
              </div>
            )}
            {prescription.notes && (
              <div>
                <h2 className="section-title mb-1">{t('prescription.notes')}</h2>
                <p className="whitespace-pre-line break-words text-gray-700">{prescription.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Actions: sticky at the bottom of the screen on phones */}
        {canFulfill && hasRemainingItems && (
          <div className="sticky bottom-0 z-10 -mx-4 space-y-2 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={handleFulfill}
                disabled={fulfillMutation.isPending}
                className="btn-primary btn-lg w-full py-3"
              >
                {t('pharmacist.view.dispenseSelected', { count: selectedCount })}
              </button>
            )}
            <button
              type="button"
              onClick={handleFulfillAll}
              disabled={fulfillMutation.isPending || isLoadingRemaining}
              className={clsx('btn-lg w-full py-3', selectedCount > 0 ? 'btn-outline' : 'btn-primary')}
            >
              {t('pharmacist.view.dispenseAll', { count: remainingCount })}
            </button>
          </div>
        )}
      </div>

      {/* Dispense confirmation */}
      {pendingDispense && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-gray-900/50 p-0 sm:items-center sm:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !fulfillMutation.isPending) setPendingDispense(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dispense-title"
            className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-md sm:rounded-2xl"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 id="confirm-dispense-title" className="text-lg font-semibold text-gray-900">
                {t('fulfillment.confirmFulfill')}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{t('pharmacist.view.confirmDescription')}</p>
            </div>
            <ul className="divide-y divide-gray-100 px-5">
              {pendingDispense.lines.map((line) => (
                <li key={line.key} className="flex items-start justify-between gap-3 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="break-words font-medium text-gray-900">{line.name}</p>
                    {line.substitute && (
                      <p className="text-xs text-success-700">
                        {t('generics.selected')}: {line.substitute}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 font-semibold tabular-nums text-gray-900">
                    {line.quantity} {line.unit || '×'}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col-reverse gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setPendingDispense(null)}
                disabled={fulfillMutation.isPending}
                autoFocus
                className="btn-secondary btn-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDispense}
                disabled={fulfillMutation.isPending}
                className="btn-primary btn-lg"
              >
                {fulfillMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('pharmacist.view.dispensing')}
                  </>
                ) : (
                  t('pharmacist.view.confirmDispense')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <DrugNoticeModal
        drugId={noticeModalDrugId}
        isOpen={!!noticeModalDrugId}
        onClose={() => setNoticeModalDrugId(null)}
      />
    </div>
  );
}
